<template>
  <div class="login">
    <img src="@/assets/img/picflow-dark-logo.svg" class="logo" alt="Picflow logo" />
    <div class="code-options">
      <div class="generate">
        <div class="hint">
          <span> Use the code below in </span>
          <span class="cms">app.picler.se</span>
        </div>
        <div class="code">
          <img
            v-if="codeGenerationLoading"
            class="button-loader"
            src="@/assets/loader-red.svg"
            alt="Loading"
          />
          <span v-else-if="generatedCode">{{ generatedCode }}</span>
        </div>
      </div>
      <div class="separator-container">
        <hr />
        <span>or</span>
        <hr />
      </div>
      <div class="enter">
        <div class="hint">
          <span> Enter the code provided to you by </span>
          <span class="cms">app.picler.se</span>
        </div>
        <form @submit.prevent="register">
          <div class="input-group">
            <input id="register-code" type="number" placeholder="Ex. 345531" v-model="code" />
            <div class="button" @click.prevent="register">
              <img
                v-if="registrationLoading"
                class="button-loader"
                src="@/assets/loader-white.svg"
                alt="Loading"
              />
              <span v-else>Apply</span>
            </div>
          </div>
        </form>
      </div>
    </div>
    <img class="water-mark" src="@/assets/img/login/water-mark.svg" alt="Picflow Watermark" />
  </div>
</template>

<script>
  import { generateScreenCode, checkValidation, validateScreenCode } from '@/api/login';
  import {
    CHECK_VALIDATION_CODE_INTERVAL,
    IS_NOTIFICATION_ALREADY_SENT,
    SCREEN_ID_ENUM,
    SCREEN_LAYOUT_ENUM,
    SESSION_TOKEN_ENUM,
  } from '@/constants';
  import { fetchScreenLayout } from '@/services/layout';
  import { handleLogin } from '@/services/auth';
  import localforage from 'localforage';
  import { refreshAuthToken } from '@/api/login';
  import { reloadPage } from '@/helpers/utils';

  export default {
    name: 'RegisterCode',
    components: {},
    data() {
      return {
        code: null,
        generatedCode: null,
        codeGenerationLoading: false,
        registrationLoading: false,
        validationInterval: null,
      };
    },
    created() {
      this.validationInterval = setInterval(
        this.checkGeneratedCodeValidation,
        CHECK_VALIDATION_CODE_INTERVAL,
      );
    },
    mounted() {
      localStorage.removeItem(IS_NOTIFICATION_ALREADY_SENT);
      this.getGeneratedCode();
      document.getElementById('register-code').focus();
      const self = this;
      document.onkeydown = function (event) {
        const keyCode = event.keyCode;
        if (keyCode === 40 || keyCode === 38) {
          if (document.getElementById('register-code')) {
            event.preventDefault();
            document.getElementById('register-code').focus();
          }
        }
      };
    },
    methods: {
      async register() {
        this.registrationLoading = true;
        try {
          const tokenData = await validateScreenCode(this.code);
          handleLogin(tokenData);
          await this.handleRedirect();
        } catch (err) {
          this.$bvToast.toast(err?.response?.data?.message || 'Unable to verify the screen code.', {
            title: 'Error!',
            solid: true,
            variant: 'danger',
          });
        } finally {
          this.registrationLoading = false;
        }
      },
      async getGeneratedCode() {
        if (!this.generatedCode) {
          this.codeGenerationLoading = true;
          try {
            const data = await generateScreenCode();
            this.generatedCode = data.code;
          } catch (error) {
            this.$bvToast.toast('Unable to generate screen code', {
              title: 'Error!',
              solid: true,
              variant: 'danger',
            });
          } finally {
            this.codeGenerationLoading = false;
          }
        }
      },
      async checkGeneratedCodeValidation() {
        if (!this.generatedCode) {
          return;
        }
        try {
          const tokenData = await checkValidation();
          if (tokenData) {
            handleLogin(tokenData);
            await this.handleRedirect();
          }
        } catch {}
      },
      async handleRedirect() {
        const routePath = this.$router.history.current.path;
        if (!routePath.includes('player')) {
          const screenId = localStorage.getItem(SCREEN_ID_ENUM);
          const result = await fetchScreenLayout(screenId);
          this.registrationLoading = false;
          this.joining = false;
          if (result.success && result.data.status === 200) {
            localStorage.setItem(SCREEN_LAYOUT_ENUM, result.data.data.layout_id);
            localforage.setItem(SCREEN_LAYOUT_ENUM, result.data.data.layout_id);
            // Reloading the page to download all the fonts and assets
            reloadPage();
          } else if (result.success && result.data.status === 204) {
            localStorage.setItem(SCREEN_LAYOUT_ENUM, '');
            localforage.setItem(SCREEN_LAYOUT_ENUM, '');
            this.$bvToast.toast('No Layout available!', {
              title: 'Warning!',
              solid: true,
              variant: 'warning',
            });
            await this.$router.push(`/no-layout`);
          } else {
            this.$bvToast.toast(result.message, {
              title: 'Error!',
              solid: true,
              variant: 'danger',
            });
            if (result.data?.status === 401) {
              await this.refreshToken();
            }
          }
        }
      },
      async refreshToken() {
        try {
          const token = await refreshAuthToken();
          localStorage.setItem(SESSION_TOKEN_ENUM, token['session_token']);
          localforage.setItem(SESSION_TOKEN_ENUM, token['session_token']);
          this.initialRefreshTokenLoading = false;
        } catch (err) {
          console.log(err);
        }
      },
    },

    beforeDestroy() {
      if (this.validationInterval) {
        clearInterval(this.validationInterval);
        this.interval = undefined;
      }
    },
  };
</script>

<style lang="scss" scoped>
  $primary-red: #e3003a;
  @mixin button-style {
    border-radius: 8px;
    background-color: #f1809d;
    color: #fff;
    font-size: 32px;
    font-weight: 500;
    line-height: 48px;
    border: none;
    padding: 16px;
    &:focus,
    &.active {
      background-color: $primary-red;
    }
    height: 100px;
    width: 680px;
  }
  @mixin active-elements-font {
    font-weight: 500;
    font-size: 42px;
    line-height: 64px;
  }
  .login {
    font-family: 'Poppins';
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    background-color: #fff7bc;
    align-items: center;
    padding-top: 80px;
    .water-mark {
      position: fixed;
      bottom: 0;
      right: 0;
    }
    .logo {
      width: 280px;
      margin-bottom: 200px;
    }
    .code-options {
      display: flex;
      flex-direction: column;
      .generate,
      .enter {
        display: flex;
        flex-direction: column;
      }
      .separator-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        opacity: 0.2;
        font-size: 32px;
        line-height: 40px;
        span {
          margin: 0 16px;
        }

        hr {
          width: 100%;
          border-top: 2px #151515 solid;
          background: none;
        }
      }
      .generate {
        margin-right: 0;
        margin-bottom: 130px;
        .code {
          @include button-style;
          @include active-elements-font;
          background-color: #feef93;
          text-align: center;
          border: 2px solid #f2df96;
          color: $primary-red;
          letter-spacing: 4px;
          width: 680px;
          margin-top: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
      .enter {
        margin-left: 0;
        margin-top: 130px;
        .input-group {
          margin-top: 24px;
          display: flex;
          flex-direction: row;
          width: 680px;
          input {
            @include active-elements-font;
            text-align: center;
            width: 70%;
            outline: none;
            border: none;
            padding: 14px 24px;
            border-radius: 8px 0 0 8px;
            border: 2px solid #d0d0d0;
            border-right: none;
            &:focus {
              border: 2px solid $primary-red;
            }
            &:focus + .button {
              background-color: $primary-red;
            }
            letter-spacing: 2px;
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
              /* display: none; <- Crashes Chrome on hover */
              -webkit-appearance: none;
              margin: 0;
              /* <-- Apparently some margin are still there even though it's hidden */
            }
          }
          .button {
            @include button-style;
            border-radius: 0 8px 8px 0;
            width: 30%;
            align-items: center;
            display: flex;
            justify-content: center;
            cursor: pointer;
          }
        }
      }
      .hint {
        font-weight: 400;
        font-size: 32px;
        line-height: 40px;
        display: flex;
        flex-direction: column;
        align-items: center;
        .cms {
          font-weight: 700;
        }
      }
      .button-loader {
        height: 32px;
      }
    }
  }
  @media screen and (min-width: 1408px) {
    .login {
      .logo {
        margin-bottom: 80px;
      }
      .code-options {
        flex-direction: row;
        .generate {
          margin-right: 80px;
          margin-bottom: 0;
          .code {
            width: 580px;
          }
        }
        .enter {
          margin-left: 80px;
          margin-top: 0;
          .input-group {
            width: 580px;
          }
        }
        .separator-container {
          display: flex;
          flex-direction: column;
          height: 75vh;
          span {
            margin: 0;
          }

          hr {
            margin: 16px 0;
            width: 0;
            border-left: 2px #151515 solid;
            border-top: none;
            background: none;
            &:first-child {
              height: 20%;
            }
            &:last-child {
              height: 80%;
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: 1407px) and (max-height: 1407px) {
    .login {
      padding-top: 64px;
      .logo {
        margin-bottom: 36px;
      }
      .code-options {
        .generate {
          margin-bottom: 36px;
          align-items: center;
          .hint {
            font-size: 24px;
          }
          .code {
            height: 64px;
            width: 436px;
            padding: 12px;
            font-size: 24px;
            line-height: 24px;
          }
        }
        .enter {
          margin-top: 36px;
          .hint {
            font-size: 24px;
          }
          .input-group {
            width: 436px;
            > input {
              padding: 0;
              font-size: 32px;
              line-height: 24px;
            }
            .button {
              height: 64px;
              padding: 12px;
              font-size: 24px;
              line-height: 24px;
            }
          }
        }
        .separator-container {
          font-size: 24px;
        }
      }
      .water-mark {
        width: 300px;
      }
    }
  }
</style>
